import { toast } from 'react-toastify';
import restFactory from 'v2/utils/restFactory';
import { buildApiError } from 'v2/utils/restFactory';
import { PLAN_ID, PERMISSIONS_PAGE_SIZE } from 'v2/utils/permissions';
import strutBackend from '../apis/strutBackend';
import {
  FETCH_CONVERSATIONS,
  UPDATE_PROPOSAL_ACTION,
  LOGIN_USER,
  LOGOUT_USER,
  AUTH_ERROR,
  FETCH_PERSISTED_USER,
  CREATE_TALKJS_SESSION,
  FETCH_SEARCH_PROPOSAL,
  SELECT_SEARCH_PROPOSAL,
  FETCH_ALL_SEARCH_PROPOSALS,
  FETCH_PROPOSALS_BY_SEARCH_ID,
  UPDATE_UNREAD_CHATS,
  CREATE_PROPOSAL_CONVERSATION,
  SAVE_DATA_REFRESH_INTERVAL,
  UPDATE_USER_PROFILE,
  FETCH_ORGANIZATION_SIGN_UP,
  GHOST_USER,
  FETCH_PERMISSIONS,
  RESET_INITIAL_DATA,
} from './types';
import { unsubscribePushNotification } from 'v2/utils/pushNotification';
import { TAppDispatch as Dispatch, TAppStore as Store } from 'store';
import { DjangoListResponse, Permission, SearchEntity, User } from 'v2/models';
import { AxiosError } from 'axios';
import { getKey } from 'v2/queries/searches/useInReviewSearchCount';

export const saveDataRefreshInterval =
  (interval: ReturnType<typeof setInterval>) => async (dispatch: Dispatch) => {
    dispatch({ type: SAVE_DATA_REFRESH_INTERVAL, payload: interval });
  };

export const fetchPermissionsAction = () => (dispatch: Dispatch) =>
  Promise.resolve().then(async () => {
    await restFactory
      .get<DjangoListResponse<Permission>>(`/dj/api/v1/permissions/`, {
        size: PERMISSIONS_PAGE_SIZE,
      })
      .then((data) => {
        dispatch({ type: FETCH_PERMISSIONS, payload: data.results });
      })
      .catch((error) => {
        if (error?.response?.status === 401) dispatch({ type: LOGOUT_USER });
      });
  });

export const fetchCheckoutNew = () => {
  return restFactory.get<any>(
    '/dj/api/v1/chargebee_subscription/get_checkout_new_hosted_page/',
    {
      plan_id: PLAN_ID.SEARCHER_BASIC,
    }
  );
};

export const fetchSubscriptionPortal = () => {
  return restFactory.get(
    '/dj/api/v1/chargebee_subscription/get_portal_session/'
  );
};

export const fetchAllProposals =
  (isBuyer = true) =>
  async (dispatch: Dispatch) => {
    await strutBackend
      .get(`/proposals?b=${isBuyer}`, {
        headers: {
          Authorization: `Bearer ${getUserToken(dispatch)}`,
        },
      })
      .then((response) => {
        dispatch({ type: FETCH_ALL_SEARCH_PROPOSALS, payload: response.data });
      })
      .catch((error) => {
        if (error?.response?.status === 401) dispatch({ type: LOGOUT_USER });
        else console.log(error);
      });
  };

export const fetchUserObject = () => async (dispatch: Dispatch) => {
  try {
    const response = await strutBackend.get('/users/me', {
      headers: {
        Authorization: `Bearer ${getUserToken(dispatch)}`,
      },
    });

    dispatch({ type: UPDATE_USER_PROFILE, payload: response.data });
  } catch (e) {
    dispatch({ type: AUTH_ERROR, payload: "You don't have a valid account" });
  }
};

export const ghostUser =
  (ghostUserEmail = null) =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch({
        type: GHOST_USER,
        payload: { ghostUserEmail },
      });

      const response = await strutBackend.get('/users/me', {
        headers: {
          Authorization: `Bearer ${getUserToken(dispatch)}`,
          ...(ghostUserEmail ? { 'Ghost-User': ghostUserEmail } : {}),
        },
      });

      if (ghostUserEmail) {
        await dispatch({ type: RESET_INITIAL_DATA });
        await fetchPermissionsAction()(dispatch);
      }
      await dispatch({
        type: GHOST_USER,
        payload: { user: response.data },
      });
    } catch (e) {
      console.log(e);
    }
  };

export const fetchProposal =
  (proposalId: number) => async (dispatch: Dispatch) => {
    const response = await strutBackend.get(`/proposals/${proposalId}`);

    dispatch({ type: FETCH_SEARCH_PROPOSAL, payload: response.data });
  };

export const selectProposal =
  (proposalObj: any) => async (dispatch: Dispatch) => {
    dispatch({ type: SELECT_SEARCH_PROPOSAL, payload: proposalObj });
  };

export const fetchProposalListBySearchId =
  (searchId: number) => async (dispatch: Dispatch) => {
    const response = await strutBackend.get(`/proposals/search=${searchId}`);

    dispatch({ type: FETCH_PROPOSALS_BY_SEARCH_ID, payload: response.data });
  };

export const fetchConversations = () => async (dispatch: Dispatch) => {
  /*
    This isn't implemented yet
    const user = JSON.parse(localStorage.getItem('user'))
    const response = await strutBackend.get(`/search-chats?user.id=${user.user.id}`,{
        headers: {
        Authorization: `Bearer ${user.jwt}`,
        },
    });
    */

  dispatch({ type: FETCH_CONVERSATIONS, payload: [] });
};

export const addUserToWaitlist =
  (userDetails: any) => async (dispatch: Dispatch) => {
    const response = await strutBackend.post(
      '/auth/local/register',
      userDetails,
      {
        headers: {
          Authorization: `Bearer ${getUserToken(dispatch)}`,
        },
      }
    );

    dispatch({ type: UPDATE_USER_PROFILE, payload: response.data });
  };

export const createTalkJSSession =
  (session: any) => async (dispatch: Dispatch) => {
    session.toJSON = () => ({ hidden: 'to help redux devtools :)' });
    dispatch({ type: CREATE_TALKJS_SESSION, payload: session });
  };

export const updateUnreadChats =
  (unreads: any) => async (dispatch: Dispatch) => {
    dispatch({ type: UPDATE_UNREAD_CHATS, payload: unreads });
  };

export const createSearch =
  (searchDetails: any) =>
  async (dispatch: Dispatch, getState: Store['getState']) => {
    try {
      const response = await createSearchAction(searchDetails)(dispatch);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.data?.[0].messages[0]?.message ||
          'Your search was not saved. Please contact support.'
      );
    }
  };

export const createSearchAction =
  (searchDetails: any) => async (dispatch: Dispatch) => {
    return await strutBackend.post('/searches', searchDetails, {
      headers: {
        Authorization: `Bearer ${getUserToken(dispatch)}`,
      },
    });
  };

export const updateSearch =
  (searchDetails: any) =>
  async (dispatch: Dispatch, getState: Store['getState']) => {
    try {
      return await updateSearchAction(searchDetails)(dispatch);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.data?.[0].messages[0]?.message ||
          'Your search was not saved. Please contact support.'
      );
    }
  };

export const updateSearchAction =
  (searchDetails: any) => async (dispatch: Dispatch) => {
    return await restFactory.put(`searches/${searchDetails.id}`, searchDetails);
  };

export const updateProposalAction =
  (
    responseId: number,
    responseAction: any,
    optionId: number,
    responseReason = ''
  ) =>
  async (dispatch: Dispatch) => {
    const response = await strutBackend.put(
      `/proposals/${responseId}`,
      {
        action: responseAction,
        response: responseReason,
        proposal_rejection_option: optionId,
      },
      {
        headers: {
          Authorization: `Bearer ${getUserToken(dispatch)}`,
        },
      }
    );
    dispatch({ type: UPDATE_PROPOSAL_ACTION, payload: response.data });
  };

export const updateUserProfileAction =
  (profileInfo: any) => async (dispatch: Dispatch) => {
    const newAvatar =
      profileInfo.avatar ||
      profileInfo.dp_updates?.avatar ||
      profileInfo.org_updates?.avatar;
    const updatingDataProvider = !!profileInfo.dp_updates;
    const updatingOrg = !!profileInfo.org_updates;
    if (newAvatar?.name) {
      const bodyFormData = new FormData();
      bodyFormData.append('files', newAvatar);
      bodyFormData.append('fileInfo', JSON.stringify({ name: newAvatar.name }));
      const [avatar] = await restFactory.post<any>(`upload`, bodyFormData, {
        'Content-Type': 'multipart/form-data',
      });
      if (updatingDataProvider) {
        profileInfo.dp_updates.avatar = { image: avatar.id };
      } else if (updatingOrg) {
        profileInfo.org_updates.avatar = { image: avatar.id };
      } else {
        profileInfo.avatar = avatar.id;
      }
    } else if (typeof newAvatar === 'string') {
      delete profileInfo.avatar;
      delete profileInfo.dp_updates?.avatar;
      delete profileInfo.org_updates?.avatar;
    }
    const user = JSON.parse(localStorage.getItem('user')!);
    const response = await strutBackend.put(
      `/users/${user.user.id}`,
      profileInfo,
      {
        headers: {
          Authorization: `Bearer ${getUserToken(dispatch)}`,
        },
      }
    );

    dispatch({ type: UPDATE_USER_PROFILE, payload: response.data });
    return response.data;
  };

export const archiveSearch =
  (searchId: number) =>
  async (dispatch: Dispatch, getState: Store['getState']) => {
    await archiveSearchAction(searchId)(dispatch);
    dispatch(fetchAllProposals());
  };

export const archiveSearchAction =
  (searchId: number) => async (dispatch: Dispatch) => {
    try {
      await strutBackend.put(
        `/searches/${searchId}`,
        { active: false },
        {
          headers: {
            Authorization: `Bearer ${getUserToken(dispatch)}`,
          },
        }
      );
    } catch (error: any) {
      toast.error(
        buildApiError(error) ||
          `Search ${searchId} can not be updated. Please contact support.`
      );
    }
  };

export const createProposalConversation =
  (proposalId: number) => async (dispatch: Dispatch) => {
    const response = await strutBackend.post(
      `/conversations`,
      {
        proposal: proposalId,
      },
      {
        headers: {
          Authorization: `Bearer ${getUserToken(dispatch)}`,
        },
      }
    );

    dispatch({ type: CREATE_PROPOSAL_CONVERSATION, payload: response.data });
  };

export const loginUserAction =
  (email: string, password: string, callback: () => void) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await strutBackend.post('/auth/local', {
        identifier: email,
        password: password,
      });

      localStorage.setItem('user', JSON.stringify(response.data));
      dispatch({ type: LOGIN_USER, payload: response.data });
      callback();
    } catch (e) {
      dispatch({ type: AUTH_ERROR, payload: 'Invalid login credentials' });
    }
  };

export const authErrorAction = (error: any) => async (dispatch: Dispatch) => {
  dispatch({ type: AUTH_ERROR, payload: error });
};

export interface JWTAndUser {
  jwt: string;
  user: User & { isInvited: boolean };
}

export const loginAuth0UserAction =
  (jwt: string, successCallback: () => void, errorCallback: () => void) =>
  async (dispatch: Dispatch) => {
    try {
      const authHeader = { Authorization: `Bearer ${jwt}` };
      const response = await strutBackend.get('/users/me', {
        headers: authHeader,
      });
      let userObj: JWTAndUser = { jwt, user: response.data };

      let isInvited = false;
      if (response.data.new_user) {
        const response = await restFactory.get<any>(
          `/dj/api/v1/invite_user/`,
          {},
          authHeader
        );
        isInvited = response.count === 1;
      }

      userObj.user.isInvited = isInvited;

      localStorage.setItem('user', JSON.stringify(userObj));
      dispatch({ type: LOGIN_USER, payload: userObj });
      successCallback();
    } catch (e) {
      dispatch({ type: AUTH_ERROR, payload: "You don't have a valid account" });
      errorCallback();
    }
  };

export const logoutUserAction = () => (dispatch: Dispatch) => {
  unsubscribePushNotification();
  dispatch({ type: LOGOUT_USER });
};

export const fetchPersistedUser = () => async (dispatch: Dispatch) => {
  dispatch({
    type: FETCH_PERSISTED_USER,
    payload: JSON.parse(localStorage.getItem('user')!),
  });
};

export const createSearchResponse =
  (proposalId: number, responseDetails: any) => async (dispatch: Dispatch) => {
    // This is the endpoint a provider uses to respond to a proposal
    const response = await strutBackend.put(
      `/proposals/${proposalId}`,
      responseDetails
    );

    dispatch({ type: UPDATE_PROPOSAL_ACTION, payload: response.data });
  };

export const createAuthenticatedSearchResponse =
  (proposalId: number, responseDetails: any) => async (dispatch: Dispatch) => {
    // This is the endpoint a provider uses to respond to a proposal

    const response = await strutBackend.put(
      `/proposals/${proposalId}`,
      responseDetails,
      {
        headers: {
          Authorization: `Bearer ${getUserToken(dispatch)}`,
        },
      }
    );

    dispatch({ type: UPDATE_PROPOSAL_ACTION, payload: response.data });
    return response.data;
  };

export const fetchMatchedOrganization = () => async (dispatch: Dispatch) => {
  const response = await strutBackend.get(`/data-providers/match`, {
    headers: {
      Authorization: `Bearer ${getUserToken(dispatch)}`,
    },
  });
  dispatch({ type: FETCH_ORGANIZATION_SIGN_UP, payload: response.data });
};

export const fetchUserSubscriptions = () => {
  return restFactory.get<any>('/dj/api/v1/chargebee_subscription/');
};

function getUserToken(dispatch: Dispatch) {
  try {
    const user = JSON.parse(localStorage.getItem('user')!);
    return user.jwt;
  } catch (error) {
    dispatch && dispatch({ type: LOGOUT_USER });
  }
}
